import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useClient, useMutation, useQuery } from 'urql';
import { Plus, Search, PencilLine, Trash2, WalletCards, Download, ArrowUp, ArrowDown, X, MoreVertical } from 'lucide-react';
import { confirmAction, showError, showSuccess } from '@/utils/alerts.js';
import { Card, CardContent } from '@/components/ui/card.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/FormComponents.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import { useUI } from '@/context/UIContext.jsx';
import AdminPageHeader from '../ui/AdminPageHeader.jsx';
import { M_DELETE_CLIENTE, Q_CLIENTES, Q_SUCURSALES_CLIENTES } from './clientes.gql.js';
import { downloadClientesXlsx } from '@/utils/clientesXlsx.js';
import AccountStatementModal from '@/components/CuentaCorriente/AccountStatementModal.jsx';
import DropdownMenu, { DropdownMenuItem } from '@/components/ui/dropdown-menu.jsx';
import { formatClienteBalance, formatClienteCuit, formatClienteDate } from './clientesList.formatters.js';

const PAGE_SIZE = 20;
const resolveClienteLocalidad = cliente => cliente?.localidad?.nombre || cliente?.localidadNombre || '—';
const resolveClienteProvincia = cliente => cliente?.provincia?.nombre || cliente?.provinciaNombre || '—';

export default function ClientesList() {
  const { setActiveModule } = useUI();
  const [searchValue, setSearchValue] = useState('');
  const [variables, setVariables] = useState({ page: 1, pageSize: PAGE_SIZE, filter: undefined });
  const [filters, setFilters] = useState({ debt: '', recent: '', localidad: '', sucursalResponsableId: '' });
  const [sort, setSort] = useState({ field: '', direction: 'asc' });
  const [exporting, setExporting] = useState(false);
  const client = useClient();
  // Las altas se realizan desde otro cliente GraphQL, por lo que su mutación no
  // invalida el caché de urql. El listado es operativo: debe consultar al servidor
  // cada vez que vuelve a montarse para no requerir una recarga manual del navegador.
  const [{ data, fetching, error }, reexecuteQuery] = useQuery({
    query: Q_CLIENTES,
    variables,
    requestPolicy: 'network-only',
  });
  const [, deleteCliente] = useMutation(M_DELETE_CLIENTE);
  const [{ data: sucursalesData }] = useQuery({ query: Q_SUCURSALES_CLIENTES, requestPolicy: 'cache-first' });
  const [deletingId, setDeletingId] = useState(null);
  const [accountClient, setAccountClient] = useState(null);

  const clientes = data?.clientes?.edges?.map(edge => edge.node) ?? [];
  const pageInfo = useMemo(() => (data?.clientes
    ? { page: data.clientes.page, pageSize: data.clientes.pageSize, totalCount: data.clientes.totalCount }
    : { page: 1, pageSize: PAGE_SIZE, totalCount: 0 }), [data?.clientes]);
  const totalPages = useMemo(() => {
    if (!pageInfo?.pageSize) {
      return 1;
    }
    return Math.max(1, Math.ceil(pageInfo.totalCount / pageInfo.pageSize));
  }, [pageInfo]);

  const activeFilter = useMemo(() => {
    const next = {};
    if (searchValue.trim()) next.search = searchValue.trim();
    if (filters.debt) next.conDeuda = filters.debt === 'yes';
    if (filters.recent) next.movimientoReciente = filters.recent === 'yes';
    if (filters.localidad.trim()) next.localidad = filters.localidad.trim();
    if (filters.sucursalResponsableId) next.sucursalResponsableId = filters.sucursalResponsableId;
    return Object.keys(next).length ? next : undefined;
  }, [filters, searchValue]);

  useEffect(() => {
    setVariables(prev => ({ ...prev, page: 1, sort: sort.field ? sort : undefined }));
  }, [sort]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setVariables(prev => ({ ...prev, filter: activeFilter, page: 1 }));
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [searchValue, activeFilter]);

  const handleClearSearch = () => {
    setSearchValue('');
    setFilters({ debt: '', recent: '', localidad: '', sucursalResponsableId: '' });
    setVariables(prev => ({ ...prev, filter: undefined, page: 1 }));
  };

  const updateFilter = (name, value) => setFilters(current => ({ ...current, [name]: value }));
  const toggleSort = field => setSort(current => ({ field, direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc' }));
  const sortIndicator = field => sort.field !== field ? null : sort.direction === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;

  const handleExport = async () => {
    setExporting(true);
    try {
      const all = [];
      let page = 1;
      let total = 0;
      do {
        const result = await client.query(Q_CLIENTES, { page, pageSize: 100, filter: activeFilter, sort: sort.field ? sort : undefined }, { requestPolicy: 'network-only' }).toPromise();
        if (result.error) throw result.error;
        const connection = result.data?.clientes;
        all.push(...(connection?.edges || []).map(edge => edge.node));
        total = connection?.totalCount || 0;
        page += 1;
      } while (all.length < total);
      downloadClientesXlsx(all);
    } catch (exportError) {
      await showError('No se pudo exportar', exportError.message || 'Intentá nuevamente.');
    } finally {
      setExporting(false);
    }
  };

  const handlePageChange = direction => {
    setVariables(prev => {
      const nextPage = direction === 'next' ? prev.page + 1 : prev.page - 1;
      const clamped = Math.min(Math.max(nextPage, 1), totalPages);
      if (clamped === prev.page) {
        return prev;
      }
      return { ...prev, page: clamped };
    });
  };

  const handleDelete = async id => {
    // Mensaje Swal unificado con tono profesional.
    const confirmation = await confirmAction({
      title: 'Confirmar eliminación',
      text: 'El cliente se eliminará de forma permanente. Esta acción no se puede deshacer.',
      confirmText: 'Eliminar cliente',
      confirmButtonColor: 'rgb(var(--brand))',
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    try {
      setDeletingId(id);
      const result = await deleteCliente({ id });
      if (result.error) {
        await showError('No pudimos eliminar el cliente', result.error.message);
        return;
      }

      if (!result.data?.deleteCliente) {
        await showError('No pudimos eliminar el cliente', 'No se recibió confirmación del servidor.');
        return;
      }

      await showSuccess('Cliente eliminado', 'El cliente fue eliminado correctamente.');

      if (clientes.length <= 1 && variables.page > 1) {
        setVariables(prev => ({ ...prev, page: prev.page - 1 }));
      } else {
        reexecuteQuery({ requestPolicy: 'network-only' });
      }
    } catch (mutationError) {
      console.error(mutationError);
      await showError('Ocurrió un error', 'No pudimos completar la eliminación. Intentá nuevamente.');
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    setActiveModule('Administración');
  }, [setActiveModule]);

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-3">
      <AdminPageHeader
        title="Clientes"
        breadcrumbs={[
          { label: 'Administración', href: '/admin' },
          { label: 'Clientes' },
        ]}
        description="Listado de clientes con búsqueda y administración básica."
        action={
          <Button
            asChild
            variant="primary"
          >
            {/* ✨ FLUJO UNIFICADO: Este botón navega al mismo componente NuevoCliente que el sidebar,
                garantizando la validación AFIP/ARCA obligatoria en todos los casos.
                Ruta: /clientes/nuevo -> componente: /pages/clientes/NuevoCliente/index.jsx
                Se eliminó el formulario alternativo (QuickCreateClienteModal) que no validaba con AFIP. */}
            <Link to="/clientes/nuevo" className="inline-flex items-center gap-2">
              <Plus size={16} />
              Nuevo Cliente
            </Link>
          </Button>
        }
      />

      <Card className="rounded-2xl">
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:gap-3">
            <div className="relative min-w-[240px] flex-1">
              <Search
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[rgba(var(--muted),0.85)]"
              />
              <Input
                value={searchValue}
                onChange={event => setSearchValue(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter') setVariables(prev => ({ ...prev, filter: activeFilter, page: 1 }));
                }}
                placeholder="Buscar por razón social, nombre o CUIT..."
                className="h-9 pl-9 pr-9"
                aria-label="Buscar clientes"
              />
              {searchValue && <button type="button" aria-label="Limpiar búsqueda" title="Limpiar búsqueda" onClick={() => setSearchValue('')} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-[rgb(var(--muted))] hover:bg-[rgba(var(--subpanel),0.8)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"><X size={14} /></button>}
            </div>
            <select aria-label="Filtrar deuda" value={filters.debt} onChange={event => updateFilter('debt', event.target.value)} className="h-9 min-w-[120px] rounded border bg-[rgb(var(--panel))] px-2 text-sm"><option value="">Deuda: todas</option><option value="yes">Con deuda</option><option value="no">Sin deuda</option></select>
            <select aria-label="Filtrar actividad" value={filters.recent} onChange={event => updateFilter('recent', event.target.value)} className="h-9 min-w-[145px] rounded border bg-[rgb(var(--panel))] px-2 text-sm"><option value="">Actividad: todas</option><option value="yes">Últimos 30 días</option><option value="no">Sin actividad reciente</option></select>
            <Input aria-label="Filtrar localidad" value={filters.localidad} onChange={event => updateFilter('localidad', event.target.value)} placeholder="Localidad" className="h-9 w-full max-w-full sm:w-[200px]" />
            <select aria-label="Filtrar sucursal responsable" value={filters.sucursalResponsableId} onChange={event => updateFilter('sucursalResponsableId', event.target.value)} className="h-9 min-w-[160px] max-w-[200px] rounded border bg-[rgb(var(--panel))] px-2 text-sm"><option value="">Sucursal: todas</option>{(sucursalesData?.sucursales || []).map(sucursal => <option key={sucursal.id} value={sucursal.id}>{sucursal.nombre}</option>)}</select>
            <div className="flex gap-2 self-start md:self-auto">
              <Button
                type="button"
                variant="ghost"
                className="text-[rgb(var(--muted))] hover:bg-[rgba(var(--subpanel),0.7)] hover:text-[rgb(var(--fg))]"
                onClick={handleClearSearch}
                disabled={fetching || (!searchValue.trim() && !Object.values(filters).some(Boolean))}
              >
                Limpiar
              </Button>
              <Button type="button" variant="ghost" onClick={handleExport} disabled={exporting || fetching}><Download size={15} /> {exporting ? 'Exportando…' : 'Exportar XLSX'}</Button>
            </div>
          </div>

          {error ? (
            <div className="rounded-2xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--panel),0.8)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-[0_10px_30px_rgba(var(--shadow),0.12)]">
              <p className="font-semibold text-[rgb(var(--brand))]">No se pudo cargar la lista de clientes.</p>
              <p className="mt-1 text-[rgba(var(--muted),0.9)]">{error.message}</p>
              <Button
                className="mt-3 border border-[rgba(var(--border),0.7)] bg-[rgba(var(--panel),0.9)] text-[rgb(var(--fg))] hover:bg-[rgba(var(--subpanel),0.85)]"
                variant="ghost"
                onClick={() => reexecuteQuery({ requestPolicy: 'network-only' })}
              >
                Reintentar
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {fetching && clientes.length === 0 ? (
                    <div className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-6 py-8 text-center text-sm text-[rgba(var(--muted),0.9)]">
                  Cargando listado de clientes...
                </div>
              ) : clientes.length === 0 ? (
                <EmptyState
                  title="Sin clientes"
                  description="No encontramos clientes con el criterio aplicado. Probá ajustar la búsqueda o crear uno nuevo."
                  action={activeFilter ? (
                    <Button type="button" variant="ghost" onClick={handleClearSearch}>Limpiar filtros</Button>
                  ) : (
                    <Button asChild variant="primary">
                      <Link to="/clientes/nuevo" className="inline-flex items-center gap-2">
                        <Plus size={16} /> Nuevo Cliente
                      </Link>
                    </Button>
                  )}
                />
              ) : (
                <>
                <div className="space-y-2 md:hidden">
                  {clientes.map(cliente => (
                    <article key={cliente.id} className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <Link to={`/admin/clientes/${cliente.id}`} className="block truncate font-semibold text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">{cliente.razonSocial || '—'}</Link>
                          {cliente.nombreFantasia?.trim() && cliente.nombreFantasia.trim().toLocaleLowerCase() !== cliente.razonSocial?.trim().toLocaleLowerCase() && <p className="truncate text-xs text-[rgb(var(--muted))]">{cliente.nombreFantasia}</p>}
                          <p className="mt-1 text-xs text-[rgb(var(--muted))]">{formatClienteCuit(cliente.cuit)} · {resolveClienteLocalidad(cliente)}</p>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          <span className="whitespace-nowrap text-sm font-semibold tabular-nums">{formatClienteBalance(cliente.saldoPendiente)}</span>
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold leading-tight ${cliente.activo ? 'border-[rgba(var(--brand),0.35)] bg-[rgba(var(--brand),0.12)] text-[rgb(var(--brand))]' : 'border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] text-[rgba(var(--muted),0.9)]'}`}>
                            {cliente.activo ? 'ACTIVO' : 'INACTIVO'}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center justify-between border-t border-[rgba(var(--border),0.4)] pt-2">
                        <span className="text-xs text-[rgb(var(--muted))]">Último movimiento: {formatClienteDate(cliente.ultimoMovimiento)}</span>
                        <div className="flex gap-1">
                          <Button type="button" variant="ghost" size="icon" aria-label={`Ver estado de cuenta de ${cliente.razonSocial || 'cliente'}`} title="Ver estado de cuenta" onClick={() => setAccountClient(cliente)}><WalletCards size={16} /></Button>
                          <Button asChild variant="ghost" size="icon" aria-label={`Editar ${cliente.razonSocial || 'cliente'}`} title="Editar cliente"><Link to={`/admin/clientes/${cliente.id}`}><PencilLine size={16} /></Link></Button>
                          <DropdownMenu
                            label={`Más acciones para ${cliente.razonSocial || 'cliente'}`}
                            trigger={<Button type="button" variant="ghost" size="icon" aria-label={`Más acciones para ${cliente.razonSocial || 'cliente'}`} title="Más acciones"><MoreVertical size={16} /></Button>}
                          >
                            {({ close }) => <DropdownMenuItem icon={Trash2} danger disabled={deletingId === cliente.id} onSelect={() => { close(); handleDelete(cliente.id); }}>Eliminar cliente</DropdownMenuItem>}
                          </DropdownMenu>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
                <div className="hidden overflow-hidden rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] md:block">
                  <table className="w-full table-fixed divide-y divide-[rgba(var(--border),0.6)] text-xs sm:text-sm">
                    <thead className="bg-[rgba(var(--subpanel),0.8)]">
                      <tr className="text-left text-xs font-semibold uppercase tracking-wide text-[rgba(var(--muted),0.9)]">
                        {[['razonSocial', 'Razón social'], ['localidad', 'Localidad'], ['saldoPendiente', 'Saldo pendiente'], ['ultimoMovimiento', 'Último movimiento'], ['sucursalResponsableNombre', 'Sucursal responsable']].map(([field, label]) => <th key={field} className="px-3 py-2"><button type="button" title={field === 'sucursalResponsableNombre' ? 'Sucursal responsable' : undefined} className="inline-flex items-center gap-1" onClick={() => toggleSort(field)}>{field === 'sucursalResponsableNombre' ? 'Sucursal' : label}{sortIndicator(field)}</button></th>)}
                        <th className="px-4 py-2">CUIT</th>
                        <th className="px-4 py-2">Teléfono</th>
                        <th className="px-4 py-2">Estado</th>
                        <th className="px-4 py-2 text-right">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[rgba(var(--border),0.4)] text-sm">
                      {clientes.map(cliente => (
                        <tr
                          key={cliente.id}
                          className="bg-[rgba(var(--panel),0.55)] transition hover:bg-[rgba(var(--brand),0.06)]"
                        >
                          <td className="max-w-0 truncate px-3 py-2 font-medium text-[rgb(var(--fg))]">
                            <div className="flex flex-col">
                              {/* ✨ LINK ENTERPRISE (SaaS): Sin subrayado permanente
                                  - no-underline + decoration-none forzado
                                  - Semibold para dar peso sin underline
                                  - Color fg normal, hover a brand
                                  - Underline solo en hover (hover:underline)
                                  - Cursor pointer implícito en <Link>
                                  - Focus visible con ring */}
                              <Link
                                to={`/admin/clientes/${cliente.id}`}
                                className="inline-flex items-center gap-2 font-semibold text-[rgb(var(--fg))] no-underline transition-colors hover:text-[rgb(var(--brand))] hover:underline hover:decoration-[rgb(var(--brand))] hover:decoration-1 hover:underline-offset-2 focus-visible:rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] focus-visible:ring-offset-1"
                                style={{ textDecoration: 'none' }}
                              >
                                {cliente.razonSocial || '—'}
                              </Link>
                              {cliente.nombreFantasia?.trim() && cliente.nombreFantasia.trim().toLocaleLowerCase() !== cliente.razonSocial?.trim().toLocaleLowerCase() ? (
                                <span className="truncate text-xs text-[rgba(var(--muted),0.85)]">{cliente.nombreFantasia}</span>
                              ) : null}
                            </div>
                          </td>
                          <td className="max-w-0 truncate px-3 py-2 text-[rgb(var(--fg))]">
                            <div className="flex flex-col">
                              <span>{resolveClienteLocalidad(cliente)}</span>
                              <span className="text-xs text-[rgba(var(--muted),0.85)]">{resolveClienteProvincia(cliente)}</span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-[rgb(var(--fg))]">{formatClienteBalance(cliente.saldoPendiente)}</td>
                          <td className="whitespace-nowrap px-3 py-2 text-[rgba(var(--muted),0.95)]">{formatClienteDate(cliente.ultimoMovimiento)}</td>
                          <td className="max-w-0 truncate px-3 py-2 text-[rgba(var(--muted),0.95)]" title={cliente.sucursalResponsableNombre || cliente.sucursalResponsableId || '—'}>{cliente.sucursalResponsableNombre || cliente.sucursalResponsableId || '—'}</td>
                          <td className="whitespace-nowrap px-3 py-2 text-[rgba(var(--muted),0.95)]">{formatClienteCuit(cliente.cuit)}</td>
                          <td className="px-4 py-2 text-[rgba(var(--muted),0.95)]">
                            {cliente.telefono || cliente.celular || '—'}
                          </td>
                          <td className="px-4 py-2">
                            <span
                              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${
                                cliente.activo
                                  ? 'border-[rgba(var(--brand),0.35)] bg-[rgba(var(--brand),0.12)] text-[rgb(var(--brand))]'
                                  : 'border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] text-[rgba(var(--muted),0.9)]'
                              }`}
                            >
                              {cliente.activo ? 'ACTIVO' : 'INACTIVO'}
                            </span>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex justify-end gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 border border-transparent p-0 text-[rgb(var(--brand))] hover:border-[rgba(var(--border),0.6)]"
                                onClick={() => setAccountClient(cliente)}
                                aria-label={`Ver estado de cuenta de ${cliente.razonSocial || 'cliente'}`}
                                title="Ver estado de cuenta"
                              >
                                <WalletCards size={16} />
                              </Button>
                              <Button
                                asChild
                                variant="ghost"
                                size="icon"
                                className="border border-transparent text-[rgba(var(--muted),0.95)] hover:border-[rgba(var(--border),0.6)] hover:bg-[rgba(var(--subpanel),0.7)] hover:text-[rgb(var(--fg))]"
                              >
                                <Link to={`/admin/clientes/${cliente.id}`} aria-label={`Editar ${cliente.razonSocial || 'cliente'}`} title="Editar cliente">
                                  <PencilLine size={16} />
                                </Link>
                              </Button>
                              <DropdownMenu
                                label={`Más acciones para ${cliente.razonSocial || 'cliente'}`}
                                trigger={(
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="border border-transparent text-[rgba(var(--muted),0.95)] hover:border-[rgba(var(--border),0.6)] hover:bg-[rgba(var(--subpanel),0.7)] hover:text-[rgb(var(--fg))]"
                                    aria-label={`Más acciones para ${cliente.razonSocial || 'cliente'}`}
                                    title="Más acciones"
                                  >
                                    <MoreVertical size={16} />
                                  </Button>
                                )}
                              >
                                {({ close }) => (
                                  <DropdownMenuItem icon={Trash2} danger disabled={deletingId === cliente.id} onSelect={() => { close(); handleDelete(cliente.id); }}>
                                    Eliminar cliente
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}

              <div className="flex flex-col gap-3 text-sm text-[rgba(var(--muted),0.95)] md:flex-row md:items-center md:justify-between">
                <span>
                  Página {pageInfo.page} de {totalPages} — {pageInfo.totalCount} clientes
                </span>
                <div className="flex gap-2">
                  <select aria-label="Tamaño de página" value={variables.pageSize} onChange={event => setVariables(prev => ({ ...prev, page: 1, pageSize: Number(event.target.value) }))} className="h-8 rounded border bg-[rgb(var(--panel))] px-2"><option value="20">20</option><option value="50">50</option><option value="100">100</option></select>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="border border-[rgba(var(--border),0.7)] bg-[rgba(var(--panel),0.9)] text-[rgb(var(--fg))] hover:bg-[rgba(var(--subpanel),0.9)]"
                    onClick={() => handlePageChange('prev')}
                    disabled={pageInfo.page <= 1 || fetching}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="border border-[rgba(var(--border),0.7)] bg-[rgba(var(--panel),0.9)] text-[rgb(var(--fg))] hover:bg-[rgba(var(--subpanel),0.9)]"
                    onClick={() => handlePageChange('next')}
                    disabled={pageInfo.page >= totalPages || fetching}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <AccountStatementModal
        cliente={accountClient}
        open={Boolean(accountClient)}
        onClose={() => setAccountClient(null)}
      />
    </div>
  );
}
